<section class="mb-8" *ngIf="isFirstLoad() || total() > 0">
  <!-- Header -->
  <div class="flex flex-wrap items-center gap-3 mb-3">
    <span nz-icon nzType="cloud-download" class="text-warning text-lg"></span>
    <h2 class="text-lg font-bold text-base-content">Awaiting Media</h2>

    <span class="badge badge-warning badge-sm font-semibold" *ngIf="!isFirstLoad()">{{ total() }}</span>

    <span
      *ngIf="stalledCount() > 0"
      class="badge badge-error badge-sm gap-1"
      nz-tooltip
      [nzTooltipTitle]="
        stalledCount() +
        ' of these have had no new footage for over half an hour — the bus may be off, out of coverage, or out of storage.'
      "
    >
      <span nz-icon nzType="warning"></span>
      {{ stalledCount() }} stalled
    </span>

    <span class="text-xs text-base-content/60 ms-auto">Refreshes every 30s</span>
  </div>

  <p class="text-sm text-base-content/70 mb-4">
    These trips are not auditable yet. Each one stays here until every crossing has every camera channel — there is no
    timeout and no partial audit, so a trip that never completes stays visible here rather than disappearing.
  </p>

  <!-- First paint only: skeletons. A refetch dims the list instead of collapsing it. -->
  <div *ngIf="isFirstLoad()" class="space-y-3" aria-hidden="true">
    <div class="card bg-base-100 border border-base-300 animate-pulse" *ngFor="let _ of [1, 2]">
      <div class="card-body py-4">
        <div class="h-4 w-1/3 rounded bg-base-300 mb-3"></div>
        <div class="h-2 w-full rounded bg-base-300 mb-2"></div>
        <div class="h-3 w-1/2 rounded bg-base-300"></div>
      </div>
    </div>
  </div>

  <!-- List -->
  <div *ngIf="!isFirstLoad()" class="space-y-3" [class.opacity-60]="collectingQuery.isFetching()">
    <article
      *ngFor="let op of operations(); trackBy: trackById"
      class="card bg-base-100 border border-base-300 shadow-sm"
      [class.border-error]="isStalled(op)"
    >
      <div class="card-body py-4 gap-3">
        <!-- Title row -->
        <div class="flex flex-wrap items-center gap-2">
          <span class="badge badge-ghost badge-sm font-mono" *ngIf="op.auditNumber">{{ op.auditNumber }}</span>
          <h3 class="font-semibold text-base-content truncate flex-1 min-w-0">{{ op.routeName }}</h3>

          <span class="badge badge-warning gap-1 flex-shrink-0">
            <span nz-icon nzType="cloud-download"></span>
            Collecting {{ op.percentComplete }}%
          </span>
        </div>

        <!-- Progress -->
        <div>
          <progress
            class="progress w-full"
            [class.progress-warning]="!isStalled(op)"
            [class.progress-error]="isStalled(op)"
            [value]="op.completeCrossings"
            [max]="op.totalCrossings || 1"
          ></progress>
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-base-content/70">
            <span class="font-medium text-base-content">
              {{ op.completeCrossings }} of {{ op.totalCrossings }} crossings complete
            </span>
            <span *ngIf="op.busName" class="flex items-center gap-1">
              <span nz-icon nzType="car"></span>{{ op.busName }}
            </span>
            <span class="flex items-center gap-1">
              <span nz-icon nzType="clock-circle"></span>waiting {{ humanMinutes(op.ageMinutes) }}
            </span>

            <!-- Nothing is shown when liveness is unknown: an unknown is not an offline. -->
            <ng-container *ngIf="busLink(op) as link">
              <span
                class="flex items-center gap-1"
                [class.text-success]="link.tone === 'success'"
                [class.text-error]="link.tone === 'error'"
              >
                <span nz-icon [nzType]="link.tone === 'success' ? 'wifi' : 'disconnect'"></span>{{ link.label }}
              </span>
            </ng-container>

            <!-- The question that decides what to do about it -->
            <span *ngIf="minutesSinceLastClip(op) !== null && !isStalled(op)" class="flex items-center gap-1 text-success">
              <span nz-icon nzType="check-circle"></span>footage arriving
            </span>
            <span *ngIf="minutesSinceLastClip(op) !== null && isStalled(op)" class="flex items-center gap-1 text-error">
              <span nz-icon nzType="warning"></span>nothing new for {{ humanMinutes(minutesSinceLastClip(op)) }}
            </span>
            <span *ngIf="minutesSinceLastClip(op) === null" class="flex items-center gap-1 text-error">
              <span nz-icon nzType="warning"></span>no footage has arrived at all
            </span>
          </div>
        </div>

        <!-- What it is waiting on. Collapsed to six by default; the rest are one click away
             rather than hidden, because "and 4 more" is exactly what someone deciding whether
             to override needs to see. -->
        <div class="rounded-lg bg-base-200 px-3 py-2" *ngIf="op.missing.length > 0">
          <p class="text-xs font-semibold text-base-content/80 mb-1">{{ waitingOn(op) }}</p>
          <ul class="text-xs text-base-content/70 space-y-0.5">
            <li
              *ngFor="let gap of (expandedId() === op.operationId ? op.missing : op.missing.slice(0, 6))"
              class="flex items-center gap-1"
            >
              <span nz-icon nzType="minus-circle" class="text-base-content/40"></span>
              {{ gapLabel(gap, op.expectedChannels) }}
            </li>
          </ul>
          <button
            *ngIf="op.missing.length > 6"
            type="button"
            class="btn btn-ghost btn-xs mt-1 px-0"
            (click)="toggleExpanded(op)"
          >
            {{ expandedId() === op.operationId ? 'Show fewer' : 'Show all ' + op.missing.length }}
          </button>
        </div>

        <!-- What recovery is doing about it. Distinguishes "being chased" from "given up on",
             which is the difference between waiting and acting. -->
        <p
          *ngIf="recoveryLine(op) as rec"
          class="flex items-center gap-1 text-xs"
          [ngClass]="rec.tone === 'error' ? 'text-error' : 'text-warning'"
        >
          <span nz-icon [nzType]="rec.tone === 'error' ? 'stop' : 'sync'"></span>{{ rec.label }}
        </p>

        <p
          *ngIf="feedback() as fb"
          [hidden]="fb.id !== op.operationId"
          class="text-xs"
          [ngClass]="fb.tone === 'error' ? 'text-error' : 'text-success'"
        >
          {{ fb.text }}
        </p>

        <!-- Actions. Asking again is safe and cheap; overriding is neither, so they do not sit
             side by side as equals. -->
        <!-- Why a disabled reason is printed rather than hovered: on 2026-10-07 a bus ran out of
             mobile data mid-trip and both buttons greyed out. Nothing on screen said why, and a
             tooltip is invisible to someone scanning a panel that refreshes every 30s. "Blocked",
             "broken" and "counting down" must not look alike. -->
        <div class="flex flex-col gap-2" *ngIf="confirmingId() !== op.operationId">
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="btn btn-sm btn-outline"
              [disabled]="!!retryBlockedReason(op) || busy() === op.operationId"
              (click)="retryNow(op)"
            >
              <span nz-icon nzType="sync" [nzSpin]="busy() === op.operationId"></span>
              Ask the bus again
            </button>
            <span *ngIf="retryBlockedReason(op) as reason" class="text-xs text-base-content/60">{{ reason }}</span>
          </div>

          <!-- The override is deliberately quieter than the safe action. It was previously the same
               size and carried text-error, which made the one irreversible control the loudest
               thing on the card — the opposite of the intent. It only takes its warning colour
               once it is actually available. -->
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="btn btn-xs btn-ghost"
              [class.text-error]="overrideUnlocked(op)"
              [class.text-base-content]="!overrideUnlocked(op)"
              [disabled]="!overrideUnlocked(op)"
              (click)="openConfirm(op)"
            >
              Audit without full evidence
            </button>
            <span *ngIf="!overrideUnlocked(op)" class="text-xs text-base-content/60">
              available in {{ minutesUntilOverride(op) }} min — give recovery a chance first
            </span>
          </div>
        </div>

        <!-- Confirmation. Deliberately concrete: the gaps being accepted, what recovery is
             still trying, and a reason that is kept with the audit forever. -->
        <div
          *ngIf="confirmingId() === op.operationId"
          class="rounded-lg border border-error/40 bg-error/5 px-3 py-3 space-y-2"
        >
          <p class="text-sm font-semibold text-error">Audit this trip without all of its footage?</p>
          <p class="text-xs text-base-content/70">
            The audit will be permanently marked as made on incomplete evidence, and the report will
            name you and show what was missing. Recovery keeps running — footage that arrives later
            still attaches.
          </p>
          <ul class="text-xs text-base-content/70 space-y-0.5">
            <li *ngFor="let gap of op.missing" class="flex items-center gap-1">
              <span nz-icon nzType="minus-circle" class="text-error/60"></span>
              {{ gapLabel(gap, op.expectedChannels) }}
            </li>
          </ul>
          <p *ngIf="recoveryLine(op) as rec" class="text-xs" [ngClass]="rec.tone === 'error' ? 'text-error' : 'text-warning'">
            {{ rec.label }}
          </p>
          <textarea
            class="textarea textarea-bordered textarea-sm w-full text-xs"
            rows="2"
            placeholder="Why are you proceeding without it? (kept with the audit)"
            [ngModel]="overrideReason()"
            (ngModelChange)="overrideReason.set($event)"
          ></textarea>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="btn btn-sm btn-error"
              [disabled]="overrideReason().trim().length < 10 || busy() === op.operationId"
              (click)="confirmForceAudit(op)"
            >
              Audit without full evidence
            </button>
            <button type="button" class="btn btn-sm btn-ghost" (click)="cancelConfirm()">Cancel</button>
          </div>
        </div>
      </div>
    </article>

    <!-- Everything complete: say so where the panel used to be, rather than vanishing silently. -->
    <buspro-empty-state
      *ngIf="operations().length === 0"
      size="compact"
      accent="success"
      icon="check-circle"
      title="No trips waiting on footage"
      description="Every trip that has arrived has all of its camera footage."
    />
  </div>
</section>
